import React from "react";
import ParallaxLayer from "../../components/ParallaxLayer";
import type { CardProps, HeroProps, SectionHeaderProps } from "../types";
import "./conjuring.css";

/**
 * THE CONJURING — darkness and what's just outside it.
 *
 * This theme has no per-character text effect on purpose. The light *is* the
 * effect: the room starts black, a match tours it, and the page is revealed by
 * being lit. A scramble on top of that would be two ideas fighting.
 */

const SNUFF_MS = 900;
/*
 * Per-leg timings.
 *
 * Each leg starts with a slow drift rather than a dead stop: the figure
 * crosses, and the match creeps after it before swinging on. A hard pause
 * reads as the animation waiting its turn; a lagging drift reads as someone
 * reacting a beat late to something they just saw.
 */
const TOUR_DRIFT_MS = 520;
const TOUR_MOVE_MS = 560;
/** Fraction of the leg covered during the slow drift. */
const DRIFT_FRACTION = 0.17;
const PASS_MS = 430;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Flame states, as a brightness multiplier.
 *
 * A CSS `alternate` flicker is a metronome — the eye locks onto the period
 * within a second and it stops being unsettling. Real candlelight is mostly
 * steady with occasional irregular disturbances, so brightness is driven from
 * JS: a slow random walk, punctuated by dips and rarer deep gutters at
 * intervals that never repeat.
 */
const FLAME = {
  /** Resting brightness wanders slowly inside this band. */
  calmMin: 0.86,
  calmMax: 1,
  /** How often the flame is disturbed at all. */
  dipEveryMin: 4200,
  dipEveryMax: 15000,
  /** A deep gutter, where the room genuinely closes in. */
  gutterChance: 0.22,
};

const rand = (min: number, max: number) => min + Math.random() * (max - min);

type Passing = { id: number; x: number; y: number; dir: 1 | -1 };

export const ConjuringHero: React.FC<HeroProps> = ({ name, tagline }) => (
  <>
    <h1 className="hero-title cj-hero-title">{name}</h1>
    <p className="hero-subtitle cj-tagline">{tagline}</p>
  </>
);

export const ConjuringSectionHeader: React.FC<SectionHeaderProps> = ({
  title,
}) => (
  <h2 className="section-heading cj-section-heading">
    <span className="cj-section-title">{title}</span>
    <span className="cj-section-rule" aria-hidden="true" />
  </h2>
);

/** Quiet documents: a hairline, a title, and the text. No boxes, no stamps. */
export const ConjuringCard: React.FC<CardProps> = ({ item }) => (
  <div className="item cj-card">
    <div className="cj-card-head">
      <h3 className="item-title cj-card-title">
        {item.href ? (
          <a
            className="item-title-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.title}
          </a>
        ) : (
          item.title
        )}
      </h3>
      {item.meta && <span className="cj-card-meta">{item.meta}</span>}
    </div>

    {item.subtitle && <p className="cj-card-role">{item.subtitle}</p>}

    <p className="item-description cj-card-description">{item.description}</p>

    {item.action && (
      <a
        href={item.action.href}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link cj-card-action"
      >
        {item.action.label}
      </a>
    )}
  </div>
);

/**
 * Waypoints for the opening sweep, as fractions of the viewport.
 *
 * Four points, so the match turns twice before settling — and it catches
 * something crossing the beam at each turn. The final point is replaced at
 * runtime with the cursor's position.
 */
const TOUR_PATH: Array<[number, number]> = [
  [0.5, 0.44],
  [0.22, 0.3],
  [0.8, 0.34],
  [0.46, 0.66],
];

const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

/**
 * The match.
 *
 * The match is the page's only light source, permanently — the room is dark
 * and the pool around the cursor is where you can see. On load the match tours
 * the room before handing control to your pointer; it gutters on its own every
 * so often; and a clap (click anywhere) blows it out before it catches again.
 *
 * The dark outside the pool is capped so body text stays readable rather than
 * disappearing — moody, not a puzzle.
 */
export const ConjuringBackdrop: React.FC = () => {
  const lightRef = React.useRef<HTMLDivElement>(null);
  const darkRef = React.useRef<HTMLDivElement>(null);
  const [snuffed, setSnuffed] = React.useState(false);
  const [passing, setPassing] = React.useState<Passing[]>([]);
  const passId = React.useRef(0);
  const [touring, setTouring] = React.useState(!prefersReducedMotion());
  const touringRef = React.useRef(touring);
  touringRef.current = touring;

  // Both layers track the match: the glow adds warmth, the dark is masked
  // open at the same point so the page is revealed by being lit.
  // Wherever the pointer last was, so the tour can hand off to it rather than
  // parking somewhere arbitrary and waiting to be found.
  const pointer = React.useRef<{ x: number; y: number } | null>(null);
  const lastLeg = React.useRef(-1);

  const place = (x: number, y: number) => {
    for (const el of [lightRef.current, darkRef.current]) {
      el?.style.setProperty("--cj-x", `${x}px`);
      el?.style.setProperty("--cj-y", `${y}px`);
    }
  };

  // Opening sweep: move, hold, move, hold, then hand off to the pointer.
  React.useEffect(() => {
    if (prefersReducedMotion()) return;

    let frame = 0;
    const start = performance.now();

    // Built each frame so the last waypoint can track the live cursor.
    const buildPath = (): Array<[number, number]> => {
      const path: Array<[number, number]> = [...TOUR_PATH];
      if (pointer.current) {
        path[path.length - 1] = [
          pointer.current.x / window.innerWidth,
          pointer.current.y / window.innerHeight,
        ];
      }
      return path;
    };

    const legs = TOUR_PATH.length - 1;
    const legMs = TOUR_DRIFT_MS + TOUR_MOVE_MS;
    const total = legs * legMs;

    const step = (now: number) => {
      // rAF hands back the frame's start time, which can predate the
      // performance.now() captured just before scheduling it. Left unclamped
      // that makes elapsed negative, leg -1, and path[leg] undefined.
      const elapsed = Math.max(0, now - start);
      const path = buildPath();

      const leg = Math.max(
        0,
        Math.min(legs - 1, Math.floor(elapsed / legMs))
      );
      const inLeg = elapsed - leg * legMs;

      let local: number;
      let drifting: boolean;
      if (inLeg < TOUR_DRIFT_MS) {
        // Creeping after whatever just went past.
        drifting = true;
        local = (inLeg / TOUR_DRIFT_MS) * DRIFT_FRACTION;
      } else {
        drifting = false;
        const t = (inLeg - TOUR_DRIFT_MS) / TOUR_MOVE_MS;
        local = DRIFT_FRACTION + easeInOut(Math.min(1, t)) * (1 - DRIFT_FRACTION);
      }

      const [ax, ay] = path[leg];
      const [bx, by] = path[leg + 1];
      const px = (ax + (bx - ax) * local) * window.innerWidth;
      const py = (ay + (by - ay) * local) * window.innerHeight;
      place(px, py);

      // The figure crosses at the top of each leg, heading the way the match
      // is about to go, so the light reads as following it.
      if (drifting && lastLeg.current !== leg) {
        lastLeg.current = leg;
        const dir: 1 | -1 = bx >= ax ? 1 : -1;
        const id = passId.current++;
        setPassing((current) => [...current, { id, x: px, y: py, dir }]);
        window.setTimeout(
          () => setPassing((current) => current.filter((p) => p.id !== id)),
          PASS_MS
        );
      }

      if (elapsed < total) frame = requestAnimationFrame(step);
      else setTouring(false);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Pointer control, ignored while the tour is still running.
  React.useEffect(() => {
    if (prefersReducedMotion()) return;

    let frame = 0;
    let pending: { x: number; y: number } | null = null;

    const apply = () => {
      frame = 0;
      if (pending && !touringRef.current) place(pending.x, pending.y);
    };

    // Coalesce to one write per frame; pointermove fires far faster than paint.
    const onMove = (event: PointerEvent) => {
      pending = { x: event.clientX, y: event.clientY };
      pointer.current = pending;
      if (!frame) frame = requestAnimationFrame(apply);
    };

    window.addEventListener("pointermove", onMove);
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // The clap.
  React.useEffect(() => {
    if (prefersReducedMotion()) return;

    let relight = 0;
    const onDown = () => {
      setSnuffed(true);
      window.clearTimeout(relight);
      relight = window.setTimeout(() => setSnuffed(false), SNUFF_MS);
    };

    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.clearTimeout(relight);
    };
  }, []);

  // The flame's own life: a slow wander, disturbed at irregular intervals.
  React.useEffect(() => {
    if (prefersReducedMotion()) return;

    let walk = 0;
    let event = 0;
    let level = 1;

    const setLevel = (value: number) => {
      level = value;
      for (const el of [lightRef.current, darkRef.current]) {
        el?.style.setProperty("--cj-flame", value.toFixed(3));
      }
    };

    // Slow drift, so the light is never perfectly still.
    const wander = () => {
      const target = rand(FLAME.calmMin, FLAME.calmMax);
      setLevel(level + (target - level) * 0.35);
      walk = window.setTimeout(wander, rand(180, 420));
    };

    // Disturbances: a quick dip, or occasionally a deep gutter.
    const disturb = () => {
      const deep = Math.random() < FLAME.gutterChance;
      const depth = deep ? rand(0.1, 0.26) : rand(0.34, 0.62);
      const hold = deep ? rand(320, 780) : rand(90, 300);

      window.clearTimeout(walk);
      setLevel(depth);

      // Guttering flames stutter on the way back rather than easing up.
      window.setTimeout(() => setLevel(depth + rand(0.1, 0.25)), hold * 0.55);
      window.setTimeout(() => {
        setLevel(rand(FLAME.calmMin, FLAME.calmMax));
        wander();
      }, hold);

      event = window.setTimeout(
        disturb,
        rand(FLAME.dipEveryMin, FLAME.dipEveryMax)
      );
    };

    wander();
    event = window.setTimeout(disturb, rand(3000, 9000));

    return () => {
      window.clearTimeout(walk);
      window.clearTimeout(event);
    };
  }, []);

  const state = snuffed ? " cj-out" : touring ? " cj-touring" : "";

  return (
    <>
      <ParallaxLayer
        className="cj-house"
        factor={0.035}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/refs/conjuring-room.jpg)`,
        }}
      />
      <div
        ref={lightRef}
        className={`cj-matchlight${state}`}
        aria-hidden="true"
      />
      <div ref={darkRef} className={`cj-dark${state}`} aria-hidden="true" />

      {passing.map((p) => (
        <div
          key={p.id}
          className="cj-passing"
          style={
            {
              left: p.x,
              top: p.y,
              "--cj-pass-dir": String(p.dir),
            } as React.CSSProperties
          }
          aria-hidden="true"
        >
          {/*
            A real photographed silhouette, thresholded to pure black — not a
            drawn path. Three attempts at hand-authoring a figure all read as
            clip art; a person's outline only looks human if it came from a
            person. Source: Unsplash (free for commercial and non-commercial
            use, no attribution required).

            It crosses fast on purpose: a static image lingering in frame reads
            as a cutout being slid past, because there is no walk cycle. Half a
            second and gone is a glimpse, which is also scarier.
          */}
          <img src={`${process.env.PUBLIC_URL}/figure-silhouette.png`} alt="" />
        </div>
      ))}
    </>
  );
};

/** Vignette, film grain, and a faint period wallpaper. */
export const ConjuringChrome: React.FC = () => (
  <>
    <div className="cj-wallpaper" aria-hidden="true" />
    <div className="cj-vignette" aria-hidden="true" />
  </>
);
